import { Box, Button } from "@material-ui/core"
import CreateNewFolderIcon from "@material-ui/icons/CreateNewFolder"
import { useEffect, useState } from "react"
import type { ReactElement } from "react"
import { connect } from "react-redux"

import { bosatlasCreateBundle, bosatlasFetchBundleList, bosatlasUpdateBundle, history } from "../../../redux"

import type { AppState, AppThunkDispatch, BosatlasAction, Bundle } from "../../../redux"
import { Body1, Header1 } from "../../atoms"
import { BackButton, BundlesSortComponent, PageContainer, TextInputDialog } from "../../molecules"
import type { ITextInputDialogProps } from "../../molecules"
import { BosatlasBundleList } from "../../organisms"

import "./bundles-page.styles.scss"
import { useTranslation } from "react-i18next"
import { useBrandName } from "../../../hooks"

export interface IBosatlasBundlePageProps {
  bundles?: Bundle[]
  fetchBundles: () => void
  createBundle: (name: string) => void
  updateBundle: (id: string, name: string) => void
}

export function BosatlasBundlesPageView(props: IBosatlasBundlePageProps): ReactElement {
  const { bundles, fetchBundles, createBundle, updateBundle } = props
  const [sortValue, setSortValue] = useState<string>("ascending")
  const [sortedList, setSortedList] = useState<Bundle[]>(bundles ? bundles : [])
  const brandName: string = useBrandName()
  const { t } = useTranslation("folder")

  const [textInputDialogState, setTextInputDialogState] = useState<ITextInputDialogProps>({
    open: false,
    title: "",
    label: "",
    onCancel: () => {},
    onClose: () => {},
    onConfirm: () => {}
  })
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    fetchBundles()
  }, [])

  useEffect(() => {
    if (bundles) {
      setSortedList(sortBundles("ascending"))
    }
  }, [bundles])

  function startBundleEdit(bundle?: Bundle): void {
    const editing: boolean = bundle !== undefined
    setTextInputDialogState({
      open: true,
      title: editing ? t("modifyFolderName") : t("createNewFolder"),
      label: t("folderName"),
      defaultValue: editing ? bundle?.title : "",
      confirmLabel: editing ? t("adjustFolder") : t("createFolder"),
      onCancel: closeTextInputDialog,
      onClose: closeTextInputDialog,
      onConfirm: (value: string) => {
        closeTextInputDialog()
        updateCreateBundle(value, bundle)
      }
    })
  }

  function handleSortChange(value: string) {
    setSortValue(value)
    setSortedList(sortBundles(value))
  }

  function sortBundles(sortMethod: string): Bundle[] {
    if (!bundles) return []
    return [...bundles].sort((a: Bundle, b: Bundle) => {
      if (a.title.toLowerCase() > b.title.toLowerCase()) return sortMethod === "ascending" ? 1 : -1
      if (a.title.toLowerCase() < b.title.toLowerCase()) return sortMethod === "ascending" ? -1 : 1
      return 0
    })
  }

  function updateCreateBundle(newTitle: string, bundle?: Bundle): void {
    if (bundle) {
      updateBundle(bundle.id as string, newTitle)
    } else {
      createBundle(newTitle)
    }
  }

  function closeTextInputDialog(): void {
    setTextInputDialogState({
      ...textInputDialogState,
      open: false
    })
  }

  function onBundleClick(bundleId: string): void {
    history.push(`/Mijn${brandName}/${bundleId}`)
  }

  return (
    <PageContainer id="BosatlasBundles">
      <TextInputDialog {...textInputDialogState} />
      <BackButton />
      <Box className="bundles-header">
        <Header1>{t("allFolders")}</Header1>
        <Button onClick={() => startBundleEdit()}>
          <CreateNewFolderIcon />
          <Body1>
            <u>{t("createNewFolder")}</u>
          </Body1>
        </Button>
      </Box>
      <BundlesSortComponent value={sortValue} changeSort={handleSortChange} />
      {sortedList && (
        <BosatlasBundleList
          noBundlesMessage={t("noFoldersYet")}
          bundles={sortedList}
          startBundleEdit={startBundleEdit}
          onViewBundleContentClick={onBundleClick}
        />
      )}
    </PageContainer>
  )
}

function mapStateToProps(state: AppState) {
  return {
    bundles: state.bosatlas.bundles
  }
}

function mapDispatchToProps(dispatch: AppThunkDispatch<BosatlasAction>) {
  return {
    fetchBundles: () => dispatch(bosatlasFetchBundleList()),
    createBundle: (name: string) => dispatch(bosatlasCreateBundle(name)),
    updateBundle: (id: string, name: string) => dispatch(bosatlasUpdateBundle(id, name))
  }
}

export const BosatlasBundlesPage = connect(mapStateToProps, mapDispatchToProps)(BosatlasBundlesPageView)
